import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPopoverAttached } from '@/core/ButtonWithPopoverAttached.molecule';
import { Button } from '@seeqdev/qomponents';
import type { FormProps } from '@/formbuilder/FormBuilder.page';
import type { ContinueButtonProps } from '@/formbuilder/formBuilder.constants';
import type { ButtonSize, ButtonVariant } from '@seeqdev/qomponents';

interface CancelSaveAndContinueProps
  extends ContinueButtonProps, Pick<FormProps, 'submitOptions' | 'resetToFirstSubmitOptionOnSubmit'> {
  submitFn?: (values: unknown) => unknown;
  cancelFn?: () => void;
  values: unknown;
  btnSize?: ButtonSize;
  btnDisabled?: boolean;
  cancelBtnLabel?: string;
  submitBtnLabel?: string;
  cancelClassNames?: string;
  submitClassNames?: string;
  cancelBtnTestId?: string;
  submitBtnTestId?: string;
  submitBtnVariant?: ButtonVariant;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  hideContinue?: boolean;
  setIsContinueButtonClicked?: (isContinueButtonClicked: boolean) => void;
}

/**
 * Cancel and Save buttons
 */
export const CancelSaveAndContinue = (props: CancelSaveAndContinueProps) => {
  const {
    submitFn,
    cancelFn,
    btnDisabled,
    values,
    cancelBtnLabel = 'CANCEL',
    submitBtnLabel,
    btnSize = 'sm',
    cancelClassNames = '',
    submitClassNames = '',
    cancelBtnTestId = 'cancelButton',
    submitBtnTestId = 'executeButton',
    submitBtnVariant = 'theme',
    hideSubmit,
    hideCancel,
    hideContinue,
    continueProcessing,
    continueButtonLabel,
    continueButtonEnabled,
    continueButtonVariant,
    continueButtonPosition,
    continueButtonExtraClassNames,
    setIsContinueButtonClicked,
    continueButtonDisabled,
    continueButtonOnSubmit,
    continueButtonIcon,
    continueButtonTooltip,
    submitOptions,
    resetToFirstSubmitOptionOnSubmit,
  } = props;

  const { t } = useTranslation();

  const [processing, setProcessing] = useState(false);
  const [continueButtonProcessing, setIsContinueButtonProcessing] = useState(false);
  const [activeSubmitOptionIndex, setActiveSubmitOptionIndex] = useState(0);

  const isClosed = useRef(false);

  const activeSubmitOption = submitOptions?.[activeSubmitOptionIndex];

  useEffect(
    () => () => {
      isClosed.current = true;
    },
    [],
  );

  const resetButtons = () => {
    setIsContinueButtonProcessing(false);
    setProcessing(false);
  };

  const manageSubmission = async (isExtraButton = false) => {
    isExtraButton ? setIsContinueButtonProcessing(true) : setProcessing(true);
    setIsContinueButtonClicked && setIsContinueButtonClicked(isExtraButton);
    if (resetToFirstSubmitOptionOnSubmit) {
      setActiveSubmitOptionIndex(0);
    }

    const functionToExecute =
      isExtraButton && continueButtonOnSubmit ? continueButtonOnSubmit : (activeSubmitOption?.submitFn ?? submitFn);

    return Promise.resolve()
      .then(() => functionToExecute?.(values))
      .finally(() => !isClosed.current && resetButtons());
  };

  const disableContinueButton = continueButtonDisabled ?? btnDisabled;

  const continueSubmitButton = (
    <Button
      id="extraSave"
      type="submit"
      onClick={() => manageSubmission(true)}
      label={continueButtonLabel && t(continueButtonLabel)}
      disabled={
        !continueButtonEnabled || continueButtonProcessing || processing || continueProcessing || disableContinueButton
      }
      size="sm"
      icon={continueButtonIcon}
      inProgress={continueButtonProcessing || continueProcessing}
      testId="extraButton"
      tooltip={continueButtonTooltip}
      extraClassNames={continueButtonExtraClassNames || 'min-width-100'}
      variant={continueButtonVariant}
    />
  );

  return (
    <>
      {!hideContinue && continueButtonPosition === 'left' && continueSubmitButton}
      {!hideCancel && (
        <Button
          onClick={() => cancelFn?.()}
          label={t(cancelBtnLabel)}
          size="sm"
          testId={cancelBtnTestId}
          extraClassNames={cancelClassNames || 'mr20 min-width-100 width-100 max-width-100'}
        />
      )}
      {!hideContinue && continueButtonPosition === 'center' && continueSubmitButton}
      {!hideSubmit &&
        (submitOptions && activeSubmitOption ? (
          <ButtonWithPopoverAttached
            id={activeSubmitOption.key}
            label={activeSubmitOption.label}
            disabled={btnDisabled}
            testId={activeSubmitOption.key}
            onClick={() => manageSubmission(false)}
            variant={activeSubmitOption?.variant ?? submitBtnVariant}
            iconPrefix="fa-solid"
            showPopover={submitOptions.length > 0}
            popoverContent={
              <div className="flexRowContainer tw:gap-1 tw:p-2">
                {submitOptions.map((option, index) => (
                  <div
                    key={option.label}
                    onClick={() => {
                      setActiveSubmitOptionIndex(index);
                      option.onSelect?.();
                    }}
                    className="cursorPointer width-maximum fs14 noActiveBg tw:border-0 tw:py-1 tw:pr-5 tw:pl-5 tw:text-left tw:dark:text-sq-text-color-dark">
                    {t(option.label)}
                  </div>
                ))}
              </div>
            }
          />
        ) : (
          <Button
            id="save"
            type="submit"
            onClick={() => manageSubmission(false)}
            label={submitBtnLabel ? t(submitBtnLabel) : t('SAVE')}
            disabled={btnDisabled || processing || continueProcessing}
            size={btnSize}
            inProgress={processing || continueProcessing}
            testId={submitBtnTestId}
            extraClassNames={submitClassNames || 'min-width-100'}
            variant={submitBtnVariant}
          />
        ))}
      {!hideContinue && continueButtonPosition === 'right' && continueSubmitButton}
    </>
  );
};
